export type VersionSentinelState = "current" | "stale" | "unavailable";

export interface VersionSentinelOptions {
  currentModuleURL: string;
  appURL: string;
  isSafeToNavigate: () => boolean;
  navigateFresh: () => void;
  onStateChange?: (state: VersionSentinelState) => void;
  fetcher?: typeof fetch;
}

export interface VersionNavigationSafetyState {
  hasMagicLinkToken: boolean;
  bookmarkDraftCount: number;
  hasUnsavedOwnerQuickEdit: boolean;
  stagedPrivateMediaCount: number;
  hasNewCardDraft: boolean;
  newCardPhotoStaging: boolean;
  newCardSaveInProgress: boolean;
  ownerBulkSaveInProgress: boolean;
  bookmarkSaveInProgress: boolean;
  receiptSaveInProgress: boolean;
  continuityOperationInFlight: boolean;
  hasConversationOutcomeUndo: boolean;
  conversationOutcomeUndoInFlight: boolean;
  hasPendingCardTurn: boolean;
  revealedCardCount: number;
  viewAllCount: number;
  conversationEventCount: number;
  hasHistoryDetail: boolean;
  cardBackPhotoDialogOpen: boolean;
}

export function isVersionNavigationSafe(
  state: VersionNavigationSafetyState,
): boolean {
  return (
    !state.hasMagicLinkToken
    && state.bookmarkDraftCount === 0
    && !state.hasUnsavedOwnerQuickEdit
    && state.stagedPrivateMediaCount === 0
    && !state.hasNewCardDraft
    && !state.newCardPhotoStaging
    && !state.newCardSaveInProgress
    && !state.ownerBulkSaveInProgress
    && !state.bookmarkSaveInProgress
    && !state.receiptSaveInProgress
    && !state.continuityOperationInFlight
    && !state.hasConversationOutcomeUndo
    && !state.conversationOutcomeUndoInFlight
    && !state.hasPendingCardTurn
    && state.revealedCardCount === 0
    && state.viewAllCount === 0
    && state.conversationEventCount === 0
    && !state.hasHistoryDetail
    && !state.cardBackPhotoDialogOpen
  );
}

const versionedAppAssetPattern =
  /^\/assets\/index-[A-Za-z0-9_-]+\.js$/;

function attributeValue(
  attributes: string,
  name: "src" | "type",
): string | null {
  const match = attributes.match(new RegExp(
    `(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'=<>]+))`,
    "i",
  ));
  return match?.[1] ?? match?.[2] ?? match?.[3] ?? null;
}

export function versionedAppAssetIdentity(
  assetURL: string,
  baseURL: string,
): string | null {
  try {
    const base = new URL(baseURL);
    const asset = new URL(assetURL, base);
    if (
      asset.origin !== base.origin
      || !versionedAppAssetPattern.test(asset.pathname)
    ) return null;
    return asset.pathname;
  } catch {
    return null;
  }
}

export function appAssetIdentityFromShell(
  html: string,
  appURL: string,
): string | null {
  const scriptTags = html.matchAll(/<script\b([^>]*)>/gi);
  for (const match of scriptTags) {
    const attributes = match[1] ?? "";
    if (attributeValue(attributes, "type")?.toLowerCase() !== "module") {
      continue;
    }
    const source = attributeValue(attributes, "src");
    if (!source) continue;
    const identity = versionedAppAssetIdentity(source, appURL);
    if (identity) return identity;
  }
  return null;
}

export class VersionSentinel {
  private readonly currentAssetIdentity: string | null;
  private readonly fetcher: typeof fetch;
  private checkInFlight: Promise<VersionSentinelState> | null = null;
  private currentState: VersionSentinelState = "unavailable";
  private navigationStarted = false;

  constructor(private readonly options: VersionSentinelOptions) {
    this.currentAssetIdentity = versionedAppAssetIdentity(
      options.currentModuleURL,
      options.appURL,
    );
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  }

  get state(): VersionSentinelState {
    return this.currentState;
  }

  check(): Promise<VersionSentinelState> {
    if (this.checkInFlight) return this.checkInFlight;
    this.checkInFlight = this.performCheck().finally(() => {
      this.checkInFlight = null;
    });
    return this.checkInFlight;
  }

  requestFreshNavigation(): boolean {
    if (
      this.currentState !== "stale"
      || this.navigationStarted
      || !this.options.isSafeToNavigate()
    ) return false;
    this.navigationStarted = true;
    this.options.navigateFresh();
    return true;
  }

  private async performCheck(): Promise<VersionSentinelState> {
    if (!this.currentAssetIdentity || this.navigationStarted) {
      return this.setState("unavailable");
    }
    try {
      const response = await this.fetcher(this.options.appURL, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: {
          accept: "text/html",
        },
      });
      const contentType = response.headers.get("content-type")
        ?.toLowerCase() ?? "";
      if (!response.ok || !contentType.includes("text/html")) {
        return this.setState("unavailable");
      }
      const responseURL = response.url
        ? new URL(response.url, this.options.appURL)
        : new URL(this.options.appURL);
      const appURL = new URL(this.options.appURL);
      if (responseURL.origin !== appURL.origin) {
        return this.setState("unavailable");
      }
      const latest = appAssetIdentityFromShell(
        await response.text(),
        this.options.appURL,
      );
      if (!latest) return this.setState("unavailable");
      if (latest === this.currentAssetIdentity) {
        return this.setState("current");
      }
      const state = this.setState("stale");
      this.requestFreshNavigation();
      return state;
    } catch {
      return this.setState("unavailable");
    }
  }

  private setState(state: VersionSentinelState): VersionSentinelState {
    if (this.currentState === state) return state;
    this.currentState = state;
    this.options.onStateChange?.(state);
    return state;
  }
}
